import { createRouter, createWebHistory } from 'vue-router';
import { recordBreadcrumb } from '@lumi/ui';
import { installRouteChunkPrefetch, installTenantlessRouteFlag, writeDemoPersona } from '@lumi/app-kit';
import { SUPPORTED_LOCALES, setLocale } from '../i18n';
import { useAuthStore } from '../stores/auth';
import { useOnboardingStore } from '../stores/onboarding';
import { useTenantsStore } from '../stores/tenants';
import { ensureFreshSession, userApi } from '../utils/api';
import { internalReturnPath, postLoginRedirectUrl } from '../utils/module-links';
import { asksForkOnSetupEntry } from '../utils/setupEntry';
import { currentTenantSlug, setStoredTenantSlug } from '../utils/runtime-config';
import { writeRequestedTenantSlug } from '../utils/requestedTenant';
import { STATUTORY_RULES_PATH, mayOpenStatutoryRules, tenantRunsTime } from '../utils/statutoryRulesAccess';
import {
  TRAINING_OCCUPATIONS_PATH,
  mayOpenTrainingOccupations,
  tenantRunsApprenticeship,
} from '../utils/trainingOccupationsAccess';
import LoginView from '../views/LoginView.vue';
import WorkspaceView from '../views/WorkspaceView.vue';
import SupportMyTicketsView from '../views/SupportMyTicketsView.vue';
import SupportTicketDetailView from '../views/SupportTicketDetailView.vue';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: WorkspaceView, meta: { requiresAuth: true } },
    // Suite onboarding (ADR 0030): the first-run fork renders chrome-less (see
    // AppChrome.isBareRoute). The setup wizard is now a MODAL over the workspace
    // (issue 214, mounted in App.vue), opened via ?setup=1 — /setup is kept as a
    // redirect for back-compat (bookmarks, the demo banner, e2e).
    {
      path: '/onboarding',
      component: () => import('../views/OnboardingForkView.vue'),
      meta: { requiresAuth: true },
    },
    { path: '/setup', redirect: () => ({ path: '/', query: { setup: '1' } }) },
    { path: '/support', component: SupportMyTicketsView, meta: { requiresAuth: true } },
    {
      path: '/support/t/:tenantSlug/:id',
      component: SupportTicketDetailView,
      meta: { requiresAuth: true },
    },
    // `tenantless: true` — the doors that work WITHOUT a resolved tenant (issue
    // 364 F57). They are served by user-api, not by a tenant-scoped module API,
    // so a `TENANT_NOT_FOUND` swallowed by some other call on the page must not
    // black them out: these are the pages a person can still use. The list lives
    // here, at the route definitions, rather than in a second copy in app-kit.
    { path: '/login', component: LoginView, meta: { tenantless: true } },
    { path: '/login/forgot', component: () => import('../views/ForgotPasswordView.vue'), meta: { tenantless: true } },
    { path: '/login/reset', component: () => import('../views/ResetPasswordView.vue'), meta: { tenantless: true } },
    {
      path: '/login/verify-email',
      component: () => import('../views/VerifyEmailView.vue'),
      meta: { tenantless: true },
    },
    { path: '/invite/accept', component: () => import('../views/AcceptInviteView.vue'), meta: { tenantless: true } },
    // The short doors an SMS links to (issue 698, ADR 0093): every character of a
    // GSM-7 message counts, so the invite and the recovery link each get a
    // one-letter path. `/i` is the same accept page; `/r` is the re-enrol half of
    // the recovery page, which `/login/recover` opens on its request half.
    { path: '/i', component: () => import('../views/AcceptInviteView.vue'), meta: { tenantless: true } },
    { path: '/login/recover', component: () => import('../views/RecoverPasskeyView.vue'), meta: { tenantless: true } },
    { path: '/r', component: () => import('../views/RecoverPasskeyView.vue'), meta: { tenantless: true } },
    { path: '/account', component: () => import('../views/AccountSecurityView.vue'), meta: { requiresAuth: true } },
    // Tenantless, and NOT requiresAuth: the confirmation link is opened from a
    // mail client, often on another device, and the swap it performs ends every
    // session the old address owned. A guard here would bounce the person to a
    // login they can no longer pass with the address they still have (#411).
    {
      path: '/account/confirm-email',
      component: () => import('../views/ConfirmEmailChangeView.vue'),
      meta: { tenantless: true },
    },
    // The undo link from the notice mailed to the address being left behind
    // (#420). Tenantless and unguarded for the same reason as the confirmation
    // above: it is opened from a mail client that holds no session, and the token
    // is the authorization.
    {
      path: '/account/cancel-email-change',
      component: () => import('../views/CancelEmailChangeView.vue'),
      meta: { tenantless: true },
    },
    {
      path: '/notifications',
      component: () => import('../views/NotificationsView.vue'),
      meta: { requiresAuth: true },
    },
    // #445 M2 — the ONE place a notification mail's button opens (ADR 0021 rule
    // 4): it follows the row the way the bell/inbox already do, never composes a
    // module URL from the mail itself. `requiresAuth` sends a signed-out visitor
    // through /login and back — see `internalReturnPath` below.
    {
      path: '/notifications/:id/open',
      component: () => import('../views/NotificationOpenView.vue'),
      meta: { requiresAuth: true },
    },
    // The consent page of an agent connection (ADR 0103 rules 9-10). user-api's
    // authorize endpoint sends the browser to `/login?returnPath=/connect/<txn>`, so
    // the workspace's own login — enforce-SSO, MFA and all — runs first, and
    // `requiresAuth` sends a signed-out visitor through it and back here.
    {
      path: '/connect/:txn',
      component: () => import('../views/ConnectAgentView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/preferences',
      component: () => import('../views/PreferencesView.vue'),
      meta: { requiresAuth: true },
    },
    // Legacy path — the notification prefs now live under the unified Preferences page.
    { path: '/notifications/settings', redirect: '/preferences' },
    // Data protection (issue 241, D20). No role in the meta and no guard beyond
    // the session: what it shows a person is their own data and their own
    // requests about it, which is not a permission anybody grants.
    {
      path: '/data-protection',
      component: () => import('../views/DataProtectionView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/billing',
      component: () => import('../views/BillingView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // The workspace's own employee login screen. Server-side the read and the
      // write are both ensureTenantAdmin (owner ∨ suite_admin); the nav edge is
      // admin-gated in AppChrome and the view renders nothing on a 403, the same
      // shape /billing uses — the route itself only needs a session.
      path: '/login-branding',
      component: () => import('../views/LoginBrandingView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Central data import/export hub (ADR 0062). Server-side every action is
      // gated per module (data_import.manage / data_export.export); the nav
      // edge is admin-gated in AppChrome, the route itself only needs auth.
      path: '/data-import',
      component: () => import('../views/DataImportHubView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Mollie hosted-checkout return: bounce back to the billing tab; the
      // reconciler confirms the mandate out-of-band (ADR 0023).
      path: '/billing/return',
      component: () => import('../views/BillingView.vue'),
      meta: { requiresAuth: true },
    },
    // ── Administration (#432) ────────────────────────────────────────────────
    //
    // The pages that used to be their own app on `{tenant}/user`. They are
    // workspace concerns — members, SSO, the audit log and support access all
    // belong to the workspace, not to a module — and the workspace already owns
    // the session, so the app they lived in existed only to re-implement it.
    // Every route only needs a session here: server-side each endpoint behind
    // them is `ensureTenantAdmin` (user-api) or the audit read guard, and the
    // nav edge is admin-gated in AppChrome — the same shape /billing and
    // /login-branding already use. `/admin/support-access` is the one
    // exception, because it is DEEP-LINKED from a notification and a mail: see
    // the fail-closed guard below.
    //
    // `/admin/policies` (Module Policies) is gone (#455 successor, ADR 0074
    // amendment): the module default role is one suite-wide rule now
    // (`DEFAULT_MODULE_ROLES`, user-api), not a per-workspace policy a screen
    // edits.
    // `/admin/users` is retired (ADR 0088 §8.2): access is managed on the Person,
    // in People. The route stays so the Admin quicklink, the account menu and old
    // bookmarks hand over to People's Rights & assignments page (#674). The
    // sidebar entry is gone (issue 687).
    {
      path: '/admin/users',
      component: () => import('../views/admin/PeopleAccessRedirectView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/sso',
      component: () => import('../views/admin/SsoSettingsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Einrichten (the full list behind the Suite Home panel): a hint page, every
      // target keeps its own checks. A person with nothing to set up gets a calm
      // "nothing here" state on the page itself, so a session is all it needs.
      path: '/admin/setup',
      component: () => import('../views/admin/SetupOverviewView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/audit',
      component: () => import('../views/admin/AuditView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Consented temporary support access (ADR 0041): the tenant-side
      // approve/revoke console. Deep-linked from the request notification and the
      // review mail, so — unlike its siblings — it carries its own guard: an
      // ordinary member following that link must land somewhere honest rather than
      // on a page that renders its "no access" state after the fact.
      path: '/admin/support-access',
      component: () => import('../views/admin/SupportAccessView.vue'),
      meta: { requiresAuth: true, requiresSuiteAdmin: true },
    },
    {
      // External AI agents (ADR 0103 rule 12, PDR 0035 rule 2): the workspace's
      // switch for members connecting their own AI client. The owner ∨ suite_admin
      // set user-api admits on the endpoints behind it, fail-closed like support
      // access above.
      path: '/admin/external-agents',
      component: () => import('../views/admin/ExternalAgentsView.vue'),
      meta: { requiresAuth: true, requiresSuiteAdmin: true },
    },
    {
      // AI usage (#433). Its nav edge is the billing set's since #665 (owner ∨
      // billing_manager ∨ suite_admin, the set /ai-usage/* admits); the route
      // itself only needs auth and the panel renders nothing on a 403.
      path: '/admin/ai-usage',
      component: () => import('../views/admin/AiUsageView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Datenverarbeitung (#430): the workspace-wide export and the blocked-
      // erasure list, moved out of the "my data" page. Deep-linkable like
      // /admin/support-access, so — unlike its Administration siblings above —
      // it carries its own guard: suite admin OR the data_protection role,
      // fail-closed the same way requiresSuiteAdmin is below.
      path: '/admin/data-processing',
      component: () => import('../views/admin/DataProcessingView.vue'),
      meta: { requiresAuth: true, requiresDataProcessingAccess: true },
    },
    {
      // Legal notices (issue 576): the employee privacy & AI notice this
      // workspace shows its own staff. Same gate as Datenverarbeitung — a
      // suite admin OR the data_protection role holder — because this is the
      // notice that authority answers for (privacy-api's
      // EmployeeNoticeAccessGuard). The notification-api link constant
      // `workspace.legalNotices.employeePrivacy` deep-links here, so this
      // path must stay exactly `/admin/legal-notices`.
      path: '/admin/legal-notices',
      component: () => import('../views/admin/LegalNoticesView.vue'),
      meta: { requiresAuth: true, requiresDataProcessingAccess: true },
    },
    {
      // Data subject requests (issue 789): the workspace's DSAR work list —
      // every open request with its Art. 12(3) deadline, the recently answered
      // ones, and the next steps privacy-api's state machine allows. Its
      // audience is privacy-api's D4 authority, the owner OR the
      // data_protection role holder, which is exactly who the DSAR reminder and
      // escalation mails reach — so it carries its own fail-closed guard
      // (`requiresDataProtectionAuthority`, below). Those mails link this page
      // (worker `WORKSPACE_DATA_REQUESTS_PATH`) and the bell line resolves to it
      // (notification-api `workspace.dataRequests`), and
      // scripts/check-dsar-chase-link.mjs reads this file for it: the path
      // must stay exactly `/admin/data-requests`.
      path: '/admin/data-requests',
      component: () => import('../views/admin/DataRequestsView.vue'),
      meta: { requiresAuth: true, requiresDataProtectionAuthority: true },
    },
    {
      // Review the Art. 15 copy (issue 820): an automatically assembled copy
      // waits here for the data-protection authority before the person is told.
      // A sibling of the work list under the same fail-closed guard — the server
      // decides again on every read and write (privacy-api's PrivacyAuthorityGuard).
      path: '/admin/data-requests/:id/review',
      component: () => import('../views/admin/DataRequestReviewView.vue'),
      meta: { requiresAuth: true, requiresDataProtectionAuthority: true },
    },
    {
      // Statutory rules & agreements (ADR 0091): the law the control plane keeps,
      // and where this workspace deviates from it. The rules are time-api's, so
      // the page speaks to it; it moved here from Time's settings because the
      // workspace owner and the suite admins answer for them as much as Time's
      // `compliance.manage` holders do. Deep-linked from the notice about a
      // changed statute (notification-api `workspace.statutoryRules`), so it
      // carries its own fail-closed guard and the path must stay exactly
      // `/admin/statutory-rules`.
      path: STATUTORY_RULES_PATH,
      component: () => import('../views/admin/StatutoryRulesView.vue'),
      meta: { requiresAuth: true, requiresStatutoryRulesAccess: true },
    },
    {
      // Training occupations (ADR 0092): which of the recognised training
      // occupations the operators keep in their catalogue this workspace offers.
      // The offer is apprentice-api's, so the page speaks to it; it sits here
      // rather than in the module's settings because the workspace owner and the
      // suite admins answer for it as much as the module's `manage` holders (the
      // head of training) do — the pattern of the statutory rules above. The
      // second kind the workspace cannot recognise on its own, so the route
      // carries the same fail-closed guard, and the path stays exactly
      // `/admin/training-occupations` for whatever links here.
      path: TRAINING_OCCUPATIONS_PATH,
      component: () => import('../views/admin/TrainingOccupationsView.vue'),
      meta: { requiresAuth: true, requiresTrainingOccupationsAccess: true },
    },
    {
      // The employee-facing counterpart (issue 576): a tenantless, unguarded
      // read of the notice's own text, reached from the login page and any
      // module footer without a session — mirrors the doors above in shape,
      // not in purpose. `tenantless` also keeps AppChrome's shell off it
      // (isBareRoute), the same reason the login family renders bare.
      path: '/legal/employee-privacy',
      component: () => import('../views/LegalNoticePublicView.vue'),
      meta: { tenantless: true },
    },
    {
      // EVERY OTHER PATH ON THIS ORIGIN (issue 364, F13). The tenant host serves
      // this SPA's index.html for anything it does not path-route to a module, so
      // a stray URL — `<tenant>/checkout`, a typo, a stale bookmark, a landing-ui
      // path someone pasted onto the wrong host — used to match NO route at all.
      // With no route there is no `meta.requiresAuth`, so the gate below never
      // fired, and `AppChrome` (which is bare only on the login family and
      // /onboarding) rendered the full shell around an empty `<RouterView>`: the
      // "phantom user" empty app. Redirecting into `/` puts every path back under
      // the one gate, so an unknown URL now ends on the workspace when there is a
      // session and on the LOGIN SCREEN when there is not.
      //
      // Query and hash are carried across on purpose: a `?token=`/`#sso_ticket=`
      // handoff that lands on an unexpected path must still be redeemed by the
      // guard rather than silently dropped here.
      path: '/:pathMatch(.*)*',
      redirect: (to) => ({ path: '/', query: to.query, hash: to.hash }),
    },
  ],
});

// Keep the workspace-unreachable blackout off the routes that work without a
// tenant (issue 364 F57). Installed FIRST, so the flag is already right when the
// guard below lets a view mount and its fetches start failing.
installTenantlessRouteFlag(router);

// Registered BEFORE the guard below (guards run in order): the view's lazy chunk
// starts downloading with the guard's reads instead of after them (issue 533).
installRouteChunkPrefetch(router);

router.beforeEach(async (to, from, next) => {
  // Feed the shared breadcrumb trail from the existing nav guard (no new hooks).
  recordBreadcrumb({ type: 'nav', to: to.fullPath });

  const authStore = useAuthStore();

  // SSO ticket handoff (ADR 0024): the central callback host redirects back here
  // with a one-time ticket in the URL fragment. Redeem it for the token pair,
  // then strip the fragment so it can't be replayed or leak into history.
  const hashParams = new URLSearchParams((to.hash || '').replace(/^#/, ''));
  const ssoTicket = hashParams.get('sso_ticket');
  const ssoError = hashParams.get('sso_error');
  if (ssoTicket) {
    try {
      // seam-exempt: transport — one-time SSO ticket redemption during session bootstrap (ADR 0024).
      const { data } = await userApi.post('/auth/sso/redeem', { ticket: ssoTicket });
      authStore.adoptSession(data);
      // Session is stored. If a module sent us through SSO, hand it back the
      // fresh token (full-page nav to its origin); otherwise land locally.
      const dest = postLoginRedirectUrl(to.query.module, to.query.returnPath);
      if (dest) {
        window.location.href = dest;
        return;
      }
      // #445 M2 — a workspace-internal deep link (no module) rides the SSO round
      // trip via `returnPath` too (see LoginView's `ssoReturnTo`). `internal` is
      // a plain path(+query) string, which `next()` resolves the same way
      // `router.push()` would — path, query AND hash all parsed correctly,
      // rather than a hand-rolled split that mishandled a second `?`, repeated
      // keys or a literal `#` in the value.
      const internal = internalReturnPath(to.query.module, to.query.returnPath);
      if (internal) {
        next(internal);
        return;
      }
      next({ path: to.path === '/login' ? '/' : to.path, hash: '', replace: true });
    } catch {
      next({ path: '/login', hash: '', query: { sso_error: 'redeem_failed' }, replace: true });
    }
    return;
  }
  if (ssoError) {
    next({ path: '/login', hash: '', query: { sso_error: ssoError }, replace: true });
    return;
  }

  // Persist the tenant slug from the SSO handoff so notification-api calls can
  // send X-Tenant-Slug even on a non-tenant host (local dev).
  //
  // Recorded as an explicit REQUEST as well, because the line above cannot express
  // one: `kb_tenant_slug` is also what the tenants store writes for the tenant it
  // picked itself, so the store could not tell a fresh `?tenantSlug=` apart from
  // its own last scope — it kept the remembered pick and overwrote the request,
  // and a link into a second tenant silently opened the first. See
  // `utils/requestedTenant.ts`; the store consumes it once, on this page load.
  const queryTenantSlug = to.query.tenantSlug ? String(to.query.tenantSlug) : '';
  if (queryTenantSlug) {
    setStoredTenantSlug(queryTenantSlug);
    writeRequestedTenantSlug(queryTenantSlug);
  }

  const queryToken = to.query.token as string | undefined;
  const queryRefreshToken = to.query.refreshToken as string | undefined;
  // The demo persona the module the user came from wore (#409): a cast
  // personId, or 'none'. Stored before the launcher's first request, so the
  // tiles and the greeting are the persona's from the first paint; a bogus
  // value is refused by the writer.
  const queryPersona = to.query.demoPersona as string | undefined;
  if (queryToken) {
    authStore.setToken(queryToken);
    if (queryRefreshToken) {
      authStore.setRefreshToken(queryRefreshToken);
    }
  }
  if (queryPersona) {
    authStore.hydrateFromToken();
    writeDemoPersona(queryTenantSlug || currentTenantSlug(), authStore.user?.userId, queryPersona);
  }
  // The language the sending app shows (issue 673): every module's switcher hands
  // it over as `?locale=`, the one carrier that also crosses an origin boundary
  // (sibling dev ports). Applied and stored like a switch made here.
  const queryLocale = typeof to.query.locale === 'string' ? to.query.locale : '';
  if (queryLocale && (SUPPORTED_LOCALES as readonly string[]).includes(queryLocale)) setLocale(queryLocale);
  // Every handoff param is consumed by now, so strip the lot from the URL —
  // including a `tenantSlug` that arrived on its own, which used to be left in the
  // bar because nothing else in this block had fired. A leftover `?tenantSlug=`
  // is not a harmless decoration: it re-asks for that tenant on every reload of
  // the page, which would quietly undo a switcher pick made since.
  if (queryToken || queryPersona || queryTenantSlug || queryLocale) {
    const cleanQuery = { ...to.query };
    delete cleanQuery.token;
    delete cleanQuery.refreshToken;
    delete cleanQuery.tenantSlug;
    delete cleanQuery.tenantName;
    delete cleanQuery.demoPersona;
    delete cleanQuery.locale;
    next({ path: to.path, query: cleanQuery, replace: true });
    return;
  }


  authStore.hydrateFromToken();

  // Repair a refresh-token-only session BEFORE the auth gates: mint a fresh
  // access token now, or drop the session so the requiresAuth gate below lands
  // on /login. Letting such a session through instead renders the workspace
  // half-dead — no tenant list, "?" avatar — because the tenants store refuses
  // to fetch without an access token and no request ever fires that could
  // trigger the 401-refresh interceptor (see ensureFreshSession).
  if (authStore.refreshToken && !(await ensureFreshSession())) {
    authStore.logout();
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Preserve a module/returnPath handoff (a module bounced an unauthenticated
    // user to the workspace root) so the post-login redirect can send them back
    // to the module they came from once they sign in.
    const loginQuery: Record<string, string> = {};
    if (typeof to.query.module === 'string') loginQuery.module = to.query.module;
    if (typeof to.query.returnPath === 'string') loginQuery.returnPath = to.query.returnPath;
    // #445 M2 — a WORKSPACE-INTERNAL deep link (e.g. a notification mail's
    // /notifications/:id/open) carries neither of the above: stamp the path
    // itself onto the same slot so the sign-in can come back to it instead of
    // stranding the visitor on the homebase (see `internalReturnPath`,
    // module-links.ts). Only when nothing already claimed it — a `returnPath`
    // already on the query is an in-flight module handoff, not this route.
    else if (to.path !== '/') loginQuery.returnPath = to.fullPath;
    // `reason` explains an involuntary bounce (the idle sign-out, issue 265).
    // A module lands on the workspace ROOT, so this gate is what forwards it to
    // /login — dropped here, the user is silently deposited on a login page with
    // no idea why, which is what the reason exists to prevent.
    if (typeof to.query.reason === 'string') loginQuery.reason = to.query.reason;
    next({ path: '/login', query: loginQuery });
    return;
  }

  // Workspace-admin-only routes (#394, moved here with the pages in #432): the same
  // owner ∨ suite_admin set user-api's ensureTenantAdmin admits on the endpoints
  // behind them. On a deep link the chrome has not loaded the tenant list yet, so it
  // is loaded here first — a fail-CLOSED read: an unloadable list carries no admin
  // flag, so the route is refused, and the server would have refused it too. Nothing
  // is decided client-side that the server does not decide again on the request.
  if (to.meta.requiresSuiteAdmin && authStore.isAuthenticated) {
    const tenantsStore = useTenantsStore();
    // `load()` reports failure through the store rather than throwing, and a
    // failed read leaves no tenant — which is the fail-closed answer.
    if (!tenantsStore.loaded) await tenantsStore.load();
    if (!tenantsStore.hasAdminRole(tenantsStore.activeTenant)) {
      next({ path: '/', replace: true });
      return;
    }
  }

  // Datenverarbeitung (#430): the same conjunction the two sections it hosts
  // gate on individually (suite admin ∨ data_protection role holder), so a
  // deep link a section itself would refuse to draw for never resolves to a
  // page with nothing on it either — same fail-closed shape as
  // requiresSuiteAdmin above.
  if (to.meta.requiresDataProcessingAccess && authStore.isAuthenticated) {
    const tenantsStore = useTenantsStore();
    if (!tenantsStore.loaded) await tenantsStore.load();
    const tenant = tenantsStore.activeTenant;
    if (!tenantsStore.hasAdminRole(tenant) && tenant?.isDataProtection !== true) {
      next({ path: '/', replace: true });
      return;
    }
  }

  // Data subject requests (issue 789): privacy-api's D4 authority — the owner
  // OR the data_protection role holder — read as the RESOLVED flag user-api
  // ships (`isDataProtectionAuthority`), never re-derived here. Fail-closed like
  // the guards above: an unloadable tenant list, a payload without the flag,
  // or a member outside the set all land on the workspace instead of on a list
  // privacy-api would refuse to fill.
  if (to.meta.requiresDataProtectionAuthority && authStore.isAuthenticated) {
    const tenantsStore = useTenantsStore();
    if (!tenantsStore.loaded) await tenantsStore.load();
    if (tenantsStore.activeTenant?.isDataProtectionAuthority !== true) {
      next({ path: '/', replace: true });
      return;
    }
  }

  // Statutory rules (ADR 0091): the workspace admin, or a Time member holding
  // `compliance.manage` — which only time-api knows, so it is asked
  // (utils/statutoryRulesAccess.ts, the same decision the sidebar entry takes).
  // Fail-closed like the two above: an unloadable tenant list, a workspace
  // without Time for a non-admin, a refusal or no answer all land on the
  // workspace instead of a page that could only refuse.
  if (to.meta.requiresStatutoryRulesAccess && authStore.isAuthenticated) {
    const tenantsStore = useTenantsStore();
    if (!tenantsStore.loaded) await tenantsStore.load();
    const tenant = tenantsStore.activeTenant;
    const admitted = !!tenant && (await mayOpenStatutoryRules({
      userId: authStore.user?.userId,
      tenantSlug: tenant.slug,
      isWorkspaceAdmin: tenantsStore.hasAdminRole(tenant),
      runsTime: tenantRunsTime(tenant),
    }));
    if (!admitted) {
      next({ path: '/', replace: true });
      return;
    }
  }

  // Training occupations (ADR 0092): the workspace admin, or a member of the
  // apprenticeship module holding `manage` — which only apprentice-api knows, so
  // it is asked (utils/trainingOccupationsAccess.ts, the same decision the
  // sidebar entry takes). Fail-closed like the three above: an unloadable tenant
  // list, a workspace without the module for a non-admin, a refusal or no answer
  // all land on the workspace instead of a page that could only refuse.
  if (to.meta.requiresTrainingOccupationsAccess && authStore.isAuthenticated) {
    const tenantsStore = useTenantsStore();
    if (!tenantsStore.loaded) await tenantsStore.load();
    const tenant = tenantsStore.activeTenant;
    const admitted = !!tenant && (await mayOpenTrainingOccupations({
      userId: authStore.user?.userId,
      tenantSlug: tenant.slug,
      isWorkspaceAdmin: tenantsStore.hasAdminRole(tenant),
      runsApprenticeship: tenantRunsApprenticeship(tenant),
    }));
    if (!admitted) {
      next({ path: '/', replace: true });
      return;
    }
  }

  if (to.path === '/login' && authStore.isAuthenticated) {
    // Already signed in but a module bounced us here — send the session back to
    // it instead of stranding the user on the workspace homebase.
    const dest = postLoginRedirectUrl(to.query.module, to.query.returnPath);
    if (dest) {
      window.location.href = dest;
      return;
    }
    // #445 M2 — already signed in, but a workspace-internal link (no module)
    // bounced us to /login regardless (a stale tab, a race on hydration).
    const internal = internalReturnPath(to.query.module, to.query.returnPath);
    if (internal) {
      next(internal);
      return;
    }
    next('/');
    return;
  }

  // Suite onboarding gate (ADR 0030; staged commit ADR 0032). Runs only for
  // authenticated app routes. `unset`/`demo_seeding` → the fork view (admin fork /
  // preparing screen / non-admin waiting). `wiping`/`applying` → the wizard's
  // commit screen at /setup (resume an in-flight commit on hard-refresh). While
  // still setting up (`setup`/`demo`, pre-commit) the fork and the wizard are both
  // opt-in — a demo tenant explores the workspace and reaches the wizard/fork on
  // its own. Once resolved, keep users out of both. Fail-open on an unreadable
  // status.
  if (to.meta.requiresAuth && authStore.isAuthenticated) {
    const onboarding = useOnboardingStore();
    const status = await onboarding.fetchStatus();
    if (status && status.mode) {
      const onFork = to.path === '/onboarding';
      const wantsSetup = to.query.setup === '1';
      const m = status.mode;
      if (status.firstRunResolved) {
        // Onboarding is done — keep users off the fork and drop a stale ?setup so
        // the wizard modal can never re-open.
        if (onFork) {
          next('/');
          return;
        }
        if (wantsSetup) {
          const query = { ...to.query };
          delete query.setup;
          next({ path: to.path, query, replace: true });
          return;
        }
      } else if (m === 'unset' || m === 'demo_seeding') {
        if (!onFork) {
          next('/onboarding');
          return;
        }
      } else if (m === 'wiping' || m === 'applying') {
        // A commit is in flight. The wizard MODAL opens itself (it watches the mode)
        // on any authenticated route for admins, so a hard-refresh mid-commit resumes
        // — just move an admin off the chrome-less fork so the modal overlays the real
        // workspace. Non-admins must NOT drive the admin-only replay (they'd 403), so
        // hold them on the fork's "being set up" waiting screen.
        if (status.isAdmin) {
          if (onFork) {
            next('/');
            return;
          }
        } else if (!onFork) {
          next('/onboarding');
          return;
        }
      } else if (
        wantsSetup &&
        !onFork &&
        from.path !== '/onboarding' &&
        asksForkOnSetupEntry({
          mode: m,
          isAdmin: status.isAdmin,
          firstRunResolved: status.firstRunResolved,
          hasStagedWork: onboarding.hasStagedWork,
        })
      ) {
        // ADR 0032 amendment (2026-09-01): setup from a LIVE DEMO asks the fork
        // (guide me vs start empty) first, unless there is staged work a re-ask
        // would put at risk. The rule itself lives in `utils/setupEntry.ts`.
        //
        // The ENFORCEMENT lives here because this guard is the one place every
        // entry into the wizard passes: the workspace demo banner's CTA, a module
        // UI's deep link to `<workspace>/?setup=1` (time-ui / people-ui /
        // backoffice-ui / apprentice-ui all redirect there), the `/setup`
        // back-compat redirect, a bookmark and a restored tab. Putting it only in
        // the banner's click handler would leave every one of those bypassing the
        // fork — which is how the wizard came to be reachable without ever passing
        // the AVV question in the first place (#364 F7).
        //
        // `from.path !== '/onboarding'` excludes the one navigation that IS the
        // answer to that question: the fork's own "guide me" pushes here right
        // after `choose-setup` (a deliberate demo no-op, mode stays `demo`,
        // nothing staged) — without this exemption the guard cannot tell that
        // explicit answer apart from a cold entry and re-asks a question that was
        // just answered, bouncing "guide me" straight back to the fork instead of
        // opening the wizard (#364 F20). A fresh page load has `from` at
        // Vue Router's START_LOCATION (path '/'), not '/onboarding', so
        // bookmarks, module deep links, the `/setup` redirect and restored tabs
        // still ask.
        next('/onboarding');
        return;
      }
      // else (setup / demo, pre-commit): the fork, the workspace and ?setup=1 (the
      // wizard modal) are all allowed.
    }
  }

  next();
});

export default router;
